import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './HastaKabul.css';

// BANKO MODÜLÜ — HASTA KABUL (kullanıcı talebi: 2026-09-21).
// VB6 karşılığı: Banko_VB6\KABUL.frm "Kabul İşlemleri".
//
// 1. AŞAMA (bu dosya): kabul listesi + filtreler + hastanın hakları + Excel.
// Sıradaki aşamalar: kabul detayı (hizmet ızgarası, Geçici Kayıt / Hasta Çıkışı),
// işlem ekleme / tahsilat / kabul iptali, çıktılar.
//
// KABUL tablosunda duran kayıtlar AÇIK kabullerdir; hasta çıkışı yapılınca kayıt
// REFERANS/ISLEMAYR/CHI'ye taşınır ve buradan düşer (VB6 ile aynı mantık).

const TARIFE_TIPI = { 1: 'Ücretli', 2: 'Kurum (seçmeli)', 3: 'Anlaşmalı Kurum' };

const para = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const trTarih = (d) => (d ? `${String(d).slice(8, 10)}.${String(d).slice(5, 7)}.${String(d).slice(0, 4)}` : '');
// Kullanılmamış hakların TAR alanı 01/01/1950 gibi yer tutucu değerler içerebiliyor; onlar boş gösterilir.
const hakTarih = (d) => (Number(String(d || '').slice(0, 4)) > 1960 ? trTarih(d) : '');
const isoTarih = (d) => (d ? String(d).slice(0, 10) : '');
const isoBugun = () => new Date().toISOString().slice(0, 10);
const isoGun = (fark) => {
  const t = new Date();
  t.setDate(t.getDate() + fark);
  return t.toISOString().slice(0, 10);
};

function HastaKabul() {
  const [secenek, setSecenek] = useState(null);
  // VB6 varsayılanı: "Sadece Bugün" (kullanıcı kararı: 2026-09-21).
  const [donem, setDonem] = useState('bugun');
  const [tar1, setTar1] = useState(isoGun(-4));     // VB6 Form_Activate: Date-4
  const [tar2, setTar2] = useState(isoGun(15));     // VB6 Form_Activate: Date+15
  const [tip, setTip] = useState('tumu');
  const [kurumKod, setKurumKod] = useState(0);
  const [hastaFiltre, setHastaFiltre] = useState('');
  const [liste, setListe] = useState([]);
  const [secili, setSecili] = useState(null);
  const [haklar, setHaklar] = useState([]);
  const [hakDurum, setHakDurum] = useState('kullanilmamis');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [detay, setDetay] = useState(null);            // açık kabul detay paneli
  const [detayYukleniyor, setDetayYukleniyor] = useState(false);
  const [bilgi, setBilgi] = useState('');
  const [hata, setHata] = useState('');

  useEffect(() => {
    api.get('/banko/kabul/secenekler')
      .then(({ data }) => setSecenek(data))
      .catch(err => setHata(err.response?.data?.hata || 'Ekran seçenekleri alınamadı.'));
  }, []);

  useEffect(() => {
    if (!bilgi) return undefined;
    const z = setTimeout(() => setBilgi(''), 6000);
    return () => clearTimeout(z);
  }, [bilgi]);

  // VB6'dan FARK: filtrenin her değişiminde liste yenilenir (VB6'da tarife/kurum seçimi
  // LST() çağırmadığı için liste eski kriterle ekranda kalıyordu).
  const listeyiYukle = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try {
      const { data } = await api.get('/banko/kabul/liste', {
        params: {
          tip,
          kurumKod: tip === 'kurum' ? kurumKod || 0 : 0,
          donem,
          tar1: donem === 'aralik' ? tar1 : undefined,
          tar2: donem === 'aralik' ? tar2 : undefined,
        },
      });
      setListe(data);
      setSecili(s => (s && data.some(k => k.masterProtNo === s.masterProtNo)
        ? data.find(k => k.masterProtNo === s.masterProtNo) : null));
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kabul listesi alınamadı.');
      setListe([]);
    } finally {
      setYukleniyor(false);
    }
  }, [tip, kurumKod, donem, tar1, tar2]);

  useEffect(() => { listeyiYukle(); }, [listeyiYukle]);

  // Seçili hastanın satış planlamadan doğan hakları (VB6 SATIS_PLAN_LST).
  useEffect(() => {
    if (!secili?.kayitNo) { setHaklar([]); return; }
    api.get('/banko/kabul/haklar', { params: { kayitNo: secili.kayitNo, durum: hakDurum } })
      .then(({ data }) => setHaklar(data))
      .catch(() => setHaklar([]));
  }, [secili, hakDurum]);

  // Hasta süzgeci listenin üzerinde çalışır: rakam yazılırsa hasta no, harf yazılırsa ad.
  const gorunen = useMemo(() => {
    const s = hastaFiltre.trim().toLocaleLowerCase('tr');
    if (!s) return liste;
    if (/^\d+$/.test(s)) return liste.filter(k => String(k.kayitNo).includes(s));
    return liste.filter(k => (k.adSoy || '').toLocaleLowerCase('tr').includes(s));
  }, [liste, hastaFiltre]);

  const toplamVaat = useMemo(
    () => gorunen.reduce((t, k) => t + (Number(k.vaatTutar) || 0), 0), [gorunen]);

  const excelAktar = async () => {
    if (gorunen.length === 0) { setHata('Listeniz boş!'); return; }
    setBilgi('Excel hazırlanıyor...');
    const ExcelJS = (await import('exceljs')).default;
    const kitap = new ExcelJS.Workbook();
    kitap.creator = 'FizyofiPlus';
    kitap.created = new Date();
    const sayfa = kitap.addWorksheet('Bekleyen Hastalar', {
      views: [{ state: 'frozen', ySplit: 3 }],
      pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const sutun = [
      { header: 'Tarih', key: 'tar', width: 12 },
      { header: 'Saat', key: 'saat', width: 8 },
      { header: 'Tedavi', key: 'tedavi', width: 10 },
      { header: 'Hasta No', key: 'kayitNo', width: 11 },
      { header: 'Hasta Adı', key: 'adSoy', width: 32 },
      { header: 'Kurumu', key: 'kurum', width: 24 },
      { header: 'Tarifesi', key: 'tarife', width: 24 },
      { header: 'Hizmet Noktası', key: 'doktor', width: 26 },
      { header: 'Branş', key: 'brans', width: 18 },
      { header: 'Hizmet', key: 'hizmet', width: 9 },
      { header: 'Vadedilen', key: 'vaatTutar', width: 14 },
      { header: 'Vade Türü', key: 'vaatTur', width: 16 },
      { header: 'Paket', key: 'paketAck', width: 30 },
    ];
    sayfa.columns = sutun;
    sayfa.spliceRows(1, 0, [], []);
    sayfa.mergeCells(1, 1, 1, sutun.length);
    sayfa.mergeCells(2, 1, 2, sutun.length);
    const b1 = sayfa.getCell('A1');
    b1.value = 'FizyofiPlus · Bekleyen Hastalar (Kabul Listesi)';
    b1.font = { size: 15, bold: true, color: { argb: 'FF1F2F46' } };
    sayfa.getRow(1).height = 24;
    const b2 = sayfa.getCell('A2');
    b2.value = `${new Date().toLocaleDateString('tr-TR')} · ${gorunen.length} kabul · vadedilen toplam ${para(toplamVaat)} TL`;
    b2.font = { size: 10, color: { argb: 'FF6B7A8C' } };

    const basSatir = sayfa.getRow(3);
    sutun.forEach((s, i) => { basSatir.getCell(i + 1).value = s.header; });
    basSatir.height = 20;
    basSatir.eachCell(h => {
      h.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      h.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2F46' } };
      h.alignment = { vertical: 'middle' };
      h.border = { bottom: { style: 'thin', color: { argb: 'FF8C99A8' } } };
    });

    gorunen.forEach((k, i) => {
      const r = sayfa.addRow({
        tar: trTarih(k.tar),
        saat: k.saat || '',
        tedavi: k.tedavi === 0 ? 'Ayaktan' : 'Yatan',
        kayitNo: k.kayitNo,
        adSoy: (k.adSoy || '').trim(),
        kurum: (k.kurum || '').trim(),
        tarife: (k.tarife || '').trim(),
        doktor: (k.doktor || '').trim(),
        brans: (k.brans || '').trim(),
        hizmet: k.hizmetSayisi,
        vaatTutar: Number(k.vaatTutar) || 0,
        vaatTur: (k.vaatTur || '').trim(),
        paketAck: (k.paketAck || '').trim(),
      });
      r.getCell('K').numFmt = '#,##0.00';
      if (i % 2) r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F6FA' } }; });
      r.eachCell(c => { c.border = { bottom: { style: 'hair', color: { argb: 'FFD6DCE4' } } }; });
    });

    sayfa.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: sutun.length } };

    const veri = await kitap.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([veri], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kabul-listesi-${isoBugun()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setBilgi(`${gorunen.length} kabul Excel'e aktarıldı.`);
  };

  // VB6 Command10_Click: kabul detay paneli. Panel liste ekranının ÜZERİNDE açılır ve yalnızca
  // "Geçici Kayıt" ya da "Hasta Çıkışı" ile kapanır (kullanıcı kararı: 2026-09-21).
  const kabulAc = async (k) => {
    if (!k) { setHata('Öncelikle hastayı seçiniz.'); return; }
    setHata(''); setDetayYukleniyor(true);
    try {
      const { data } = await api.get(`/banko/kabul/${k.masterProtNo}/detay`);
      setDetay(data);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kabul detayı açılamadı.');
    } finally {
      setDetayYukleniyor(false);
    }
  };

  const detayKapat = async (mesaj) => {
    setDetay(null);
    if (mesaj) setBilgi(mesaj);
    await listeyiYukle();
  };

  return (
    <div className="hkb">
      <div className="hkb-serit">
        <h2 className="hkb-baslik">Hasta Kabul <span>· Banko Modülü</span></h2>
        <div className={`hkb-durum ${hata ? 'hata' : bilgi ? 'bilgi' : ''}`}>
          {hata || bilgi || 'HATIRLATMA: Her sabah günlük listeye bakın, vadedilen ödemesi olanlardan ödeme almayı unutmayın.'}
        </div>
      </div>

      <div className="hkb-filtre">
        <div className="hkb-filtre-grup">
          <span className="hkb-etiket">Dönem</span>
          <div className="hkb-secim">
            {[['bugun', 'Sadece Bugün'], ['son7', 'Son 7 Gün'], ['aralik', 'Tarih Seç'], ['tumu', 'Tümü']].map(([k, ad]) => (
              <button key={k} type="button" className={`hkb-sec ${donem === k ? 'aktif' : ''}`}
                onClick={() => setDonem(k)}>{ad}</button>
            ))}
          </div>
          {donem === 'aralik' && (
            <div className="hkb-tarihler">
              <input type="date" value={tar1} onChange={e => setTar1(e.target.value)} />
              <span>–</span>
              <input type="date" value={tar2} onChange={e => setTar2(e.target.value)} />
            </div>
          )}
        </div>

        <div className="hkb-filtre-grup">
          <span className="hkb-etiket">Tarife</span>
          <div className="hkb-secim">
            {[['tumu', 'Tümü'], ['ucretli', 'Ücretli'], ['kurum', 'Kurum Hastası']].map(([k, ad]) => (
              <button key={k} type="button" className={`hkb-sec ${tip === k ? 'aktif' : ''}`}
                onClick={() => setTip(k)}>{ad}</button>
            ))}
          </div>
          {tip === 'kurum' && (
            <select value={kurumKod} onChange={e => setKurumKod(Number(e.target.value))} className="hkb-kurum">
              <option value={0}>Tüm anlaşmalı kurumlar</option>
              {(secenek?.kurumlar || []).map(k => (
                <option key={k.kayitNo} value={k.kayitNo}>{k.adi}</option>
              ))}
            </select>
          )}
        </div>

        <div className="hkb-filtre-grup hkb-filtre-hasta">
          <span className="hkb-etiket">Hasta</span>
          <input value={hastaFiltre} onChange={e => setHastaFiltre(e.target.value)}
            placeholder="Ad soyad ya da hasta no" />
          {hastaFiltre && <button type="button" className="hkb-temizle" onClick={() => setHastaFiltre('')}>✕</button>}
        </div>
      </div>

      <div className="hkb-duzen">
        <div className="hkb-sol card">
          <div className="hkb-liste">
            <table>
              <thead>
                <tr>
                  <th>Tarih</th><th>Saat</th><th>Durum</th><th>Hasta No</th><th>Hasta Adı</th>
                  <th>Kurumu</th><th>Hizmet Noktası</th><th className="sag">Hizmet</th>
                  <th className="sag">Vadedilen</th><th>Vade Türü</th>
                </tr>
              </thead>
              <tbody>
                {yukleniyor && <tr><td colSpan={10} className="hkb-bos">Liste yükleniyor...</td></tr>}
                {!yukleniyor && gorunen.length === 0 && (
                  <tr><td colSpan={10} className="hkb-bos">Bu kriterlerde bekleyen kabul yok.</td></tr>
                )}
                {!yukleniyor && gorunen.map(k => (
                  <tr key={k.masterProtNo}
                    className={`${secili?.masterProtNo === k.masterProtNo ? 'secili' : ''} ${k.tarfTp === 1 ? '' : 'kurumlu'}`}
                    onClick={() => setSecili(k)}
                    onDoubleClick={() => kabulAc(k)}>
                    <td>{trTarih(k.tar)}</td>
                    <td>{k.saat}</td>
                    <td className={k.geldi === 1 ? 'hkb-geldi' : 'hkb-bekliyor'}>{k.geldi === 1 ? 'GELDİ' : 'Bekliyor'}</td>
                    <td>{k.kayitNo}</td>
                    <td className="hkb-ad">{k.adSoy}</td>
                    <td>{k.tarfTp === 1 ? 'ÜCRETLİ' : k.kurum}</td>
                    <td>{k.doktor}</td>
                    <td className="sag">{k.hizmetSayisi}</td>
                    <td className="sag">{k.vaatTutar ? para(k.vaatTutar) : ''}</td>
                    <td>{k.vaatTur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hkb-sayac">
            {gorunen.length} kabul{toplamVaat > 0 ? ` · vadedilen toplam ${para(toplamVaat)} TL` : ''}
            {secili ? ` · seçili: ${secili.adSoy}` : ''}
          </div>

          <div className="hkb-islemler">
            <button type="button" className="hkb-islem yenile" onClick={listeyiYukle} title="Aktif hastaları listele">
              <b>🔄</b><span>YENİLE</span>
            </button>
            <button type="button" className="hkb-islem excel" onClick={excelAktar} title="Listeyi Excel'e aktar">
              <b>📊</b><span>EXCEL'E AKTAR</span>
            </button>
            <button type="button" className="hkb-islem kabul" onClick={() => kabulAc(secili)} disabled={!secili}
              title="Seçili hastanın kabul işlemini aç">
              <b>🧾</b><span>KABUL İŞLEMİNİ YAP</span>
            </button>
          </div>
        </div>

        <div className="hkb-sag card">
          <span className="hkb-kutu-baslik">Hastanın Satış Planlamadan Doğan Hakları</span>
          {!secili && <p className="hkb-bos-not">Soldaki listeden bir kabul seçiniz.</p>}
          {secili && (
            <>
              <div className="hkb-hasta-bas">
                <strong>{secili.adSoy}</strong>
                <span>Hasta No: {secili.kayitNo}</span>
                <span>{TARIFE_TIPI[secili.tarfTp] || '—'}{secili.tarife ? ` · ${secili.tarife}` : ''}</span>
              </div>

              <div className="hkb-hak-filtre">
                {[['kullanilmamis', 'Kullanılmamış'], ['kullanilmis', 'Kullanılmış'], ['tumu', 'Tümü']].map(([k, ad]) => (
                  <button key={k} type="button" className={`hkb-sec ${hakDurum === k ? 'aktif' : ''}`}
                    onClick={() => setHakDurum(k)}>{ad}</button>
                ))}
                <span className="hkb-hak-adet">{haklar.length} adet</span>
              </div>

              <div className="hkb-haklar">
                <table>
                  <thead>
                    <tr><th>Tarih</th><th>Hizmet</th><th className="sag">Fiyatı</th><th className="sag">Tahsilat</th><th>Durum</th></tr>
                  </thead>
                  <tbody>
                    {haklar.length === 0 && <tr><td colSpan={5} className="hkb-bos">Bu süzgeçte hak yok.</td></tr>}
                    {haklar.map(h => (
                      <tr key={h.id} className={h.onay === 1 ? 'kullanilmis' : ''}>
                        <td>{hakTarih(h.tar)}</td>
                        <td className="hkb-ad">{h.hizmet}</td>
                        <td className="sag">{para(h.birimUcret)}</td>
                        <td className="sag">{para(h.odenen)}</td>
                        <td>{h.onay === 1 ? 'Kullanıldı' : 'Bekliyor'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {secili.paketAck && (
                <div className="hkb-paket"><b>Paket:</b> {secili.paketAck}</div>
              )}
              {secili.bankoNot && (
                <div className="hkb-banko-not"><b>Banko Notu:</b> {secili.bankoNot}</div>
              )}
              {secili.tahsilatYasagi === 1 && (
                <div className="hkb-uyari">Bu seansın tahsilatı SATIŞ ESNASINDA alınmıştır; kayıtta değişiklik yapılamaz.</div>
              )}
            </>
          )}
        </div>
      </div>

      {detayYukleniyor && (
        <div className="hkb-detay-zemin"><div className="hkb-detay-yukleniyor">Kabul açılıyor...</div></div>
      )}
      {detay && <KabulDetayi detay={detay} secenek={secenek} onKapat={detayKapat} />}
    </div>
  );
}


// --- ÇIKTILAR (VB6 Command11/Command16 proforma, Command38/39 kabul raporu,
//     Command35 ön sigorta formu, Command34 barkod) ------------------------------------------
// VB6 bunları Excel COM / BarTender / RAPORSABLONLAR şablonlarıyla üretiyordu; web'de Excel
// çıktısı ExcelJS ile, basılı formlar yazdırma penceresiyle üretilir.

const yazdirPenceresi = (baslik, govde) => {
  const p = window.open('', '_blank', 'width=900,height=700');
  if (!p) { window.alert('Yazdırma penceresi açılamadı; tarayıcınızın açılır pencere engelini kapatınız.'); return null; }
  p.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${baslik}</title>
<style>
  body { font-family: Segoe UI, Arial, sans-serif; font-size: 12px; color: #1F2F46; margin: 24px; }
  h1 { font-size: 17px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 16px 0 6px; color: #3E63A8; }
  .ust { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #1F2F46; padding-bottom: 8px; }
  .kutu { display: grid; grid-template-columns: 130px 1fr; gap: 2px 10px; margin-top: 8px; }
  .kutu b { font-weight: 600; color: #6B7A8C; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #1F2F46; color: #fff; text-align: left; padding: 5px 7px; font-size: 11px; }
  td { padding: 4px 7px; border-bottom: 1px solid #DDE3EB; }
  .sag { text-align: right; }
  .toplamlar { margin-top: 10px; margin-left: auto; width: 280px; }
  .toplamlar div { display: flex; justify-content: space-between; padding: 3px 0; }
  .toplamlar .genel { border-top: 1px solid #1F2F46; font-weight: 700; font-size: 14px; padding-top: 5px; }
  .imza { margin-top: 40px; display: flex; justify-content: space-between; }
  .kvkk { margin-top: 20px; font-size: 9.5px; color: #6B7A8C; line-height: 1.45; }
  @media print { body { margin: 10mm; } }
</style></head><body>${govde}
<script>window.onload = function () { window.print(); };${'<'}/script></body></html>`);
  p.document.close();
  return p;
};

const proformaExcel = async (d, hasta) => {
  const ExcelJS = (await import('exceljs')).default;
  const kitap = new ExcelJS.Workbook();
  kitap.creator = 'FizyofiPlus';
  const sayfa = kitap.addWorksheet(hasta ? 'Hasta Proforma' : 'Kurum Proforma');
  sayfa.columns = [
    { header: 'Hizmet', key: 'hizmet', width: 40 },
    { header: 'Adet', key: 'adet', width: 8 },
    { header: 'Birim Fiyat', key: 'brm', width: 14 },
    { header: 'KDV %', key: 'kdv', width: 8 },
    { header: 'Tutar', key: 'tutar', width: 14 },
  ];
  sayfa.spliceRows(1, 0, [], [], [], [], []);
  sayfa.getCell('A1').value = hasta ? 'PROFORMA FATURA (HASTA PAYI)' : 'PROFORMA FATURA (KURUM PAYI)';
  sayfa.getCell('A1').font = { size: 14, bold: true };
  sayfa.getCell('A2').value = `${d.adSoy}  (Hasta No: ${d.kayitNo})`;
  sayfa.getCell('A3').value = d.adres || '';
  sayfa.getCell('A4').value = `Tarih: ${trTarih(d.tar)} · Tarife: ${d.tarife || ''}${d.kurum ? ' · Kurum: ' + d.kurum : ''}`;
  const bas = sayfa.getRow(6);
  ['Hizmet', 'Adet', 'Birim Fiyat', 'KDV %', 'Tutar'].forEach((h, i) => { bas.getCell(i + 1).value = h; });
  bas.eachCell(c => {
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2F46' } };
  });
  d.satirlar.forEach(s => {
    const r = sayfa.addRow({
      hizmet: s.hizmet, adet: s.adet,
      brm: hasta ? s.birimHasta : s.birimKurum, kdv: s.kdvYd,
      tutar: hasta ? s.tutarHasta : s.tutarKurum,
    });
    ['C', 'E'].forEach(k => { r.getCell(k).numFmt = '#,##0.00'; });
  });
  sayfa.addRow({});
  const toplam = sayfa.addRow({ hizmet: 'TOPLAM', tutar: hasta ? d.toplamHasta : d.toplamKurum });
  toplam.getCell('E').numFmt = '#,##0.00';
  (hasta ? d.kdvHasta : d.kdvKurum).forEach(x => {
    const r = sayfa.addRow({ hizmet: `KDV %${x.oran}`, tutar: x.tutar });
    r.getCell('E').numFmt = '#,##0.00';
  });
  const genel = sayfa.addRow({ hizmet: 'GENEL TOPLAM', tutar: hasta ? d.genelToplamHasta : d.genelToplamKurum });
  genel.font = { bold: true };
  genel.getCell('E').numFmt = '#,##0.00';

  const veri = await kitap.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([veri], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `proforma-${hasta ? 'hasta' : 'kurum'}-${d.masterProtNo}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

const kabulRaporu = (d, hasta) => {
  const satirlar = d.satirlar.map(s => `<tr>
      <td>${trTarih(s.tar)}</td><td>${s.hizmet}</td><td class="sag">${s.adet}</td>
      <td class="sag">${para(hasta ? s.birimHasta : s.birimKurum)}</td>
      <td class="sag">${s.kdvYd}</td>
      <td class="sag">${para(hasta ? s.tutarHasta : s.tutarKurum)}</td></tr>`).join('');
  const kdvler = (hasta ? d.kdvHasta : d.kdvKurum)
    .map(x => `<div><span>KDV %${x.oran}</span><span>${para(x.tutar)} TL</span></div>`).join('');
  yazdirPenceresi('Kabul Raporu', `
    <div class="ust">
      <div><h1>KABUL RAPORU</h1><div>${hasta ? 'Hasta Payı' : 'Kurum Payı'}</div></div>
      <div style="text-align:right"><div>Protokol: <b>${d.masterProtNo}</b></div><div>${trTarih(d.tar)}</div></div>
    </div>
    <div class="kutu">
      <b>Hasta</b><span>${d.adSoy} (${d.kayitNo})</span>
      <b>TC Kimlik</b><span>${d.tcKimlik || '-'}</span>
      <b>Telefon</b><span>${d.tel || '-'}</span>
      <b>Adres</b><span>${d.adres || '-'}</span>
      <b>Tarife</b><span>${d.tarife || '-'}</span>
      ${d.kurum ? `<b>Kurum</b><span>${d.kurum}</span>` : ''}
      <b>Hizmet Noktası</b><span>${d.doktor || '-'}</span>
      ${d.bankoNot ? `<b>Banko Notu</b><span>${d.bankoNot}</span>` : ''}
    </div>
    <h2>Yapılan İşlemler</h2>
    <table><thead><tr><th>Tarih</th><th>Hizmet</th><th class="sag">Adet</th><th class="sag">B.Fiyat</th><th class="sag">KDV %</th><th class="sag">Tutar</th></tr></thead>
    <tbody>${satirlar}</tbody></table>
    <div class="toplamlar">
      <div><span>TOPLAM</span><span>${para(hasta ? d.toplamHasta : d.toplamKurum)} TL</span></div>
      ${kdvler}
      <div class="genel"><span>GENEL TOPLAM</span><span>${para(hasta ? d.genelToplamHasta : d.genelToplamKurum)} TL</span></div>
      ${hasta ? `<div><span>ALINAN</span><span>${para(d.alinan)} TL</span></div>
                 <div><span>KALAN</span><span>${para(d.kalan)} TL</span></div>` : ''}
    </div>
    <div class="imza"><div>Hasta / Yakını İmza</div><div>Yetkili İmza</div></div>
    <div class="kvkk">Bu belge proforma niteliğindedir, mali değeri yoktur.
    Kişisel verileriniz 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında işlenmektedir.</div>`);
};

const onSigortaFormu = (d) => {
  yazdirPenceresi('Ön Sigorta Bilgi Formu', `
    <div class="ust">
      <div><h1>ÖN SİGORTA BİLGİ FORMU</h1><div>${d.kurum || ''}</div></div>
      <div style="text-align:right"><div>Protokol: <b>${d.masterProtNo}</b></div><div>${trTarih(d.tar)}</div></div>
    </div>
    <div class="kutu">
      <b>Hasta</b><span>${d.adSoy}</span>
      <b>Hasta No</b><span>${d.kayitNo}</span>
      <b>TC Kimlik</b><span>${d.tcKimlik || '-'}</span>
      <b>Doğum Tarihi</b><span>${trTarih(d.dogTar) || '-'}</span>
      <b>Cinsiyet</b><span>${d.cins || '-'}</span>
      <b>Telefon</b><span>${d.tel || '-'}</span>
      <b>Adres</b><span>${d.adres || '-'}</span>
      <b>Kurum</b><span>${d.kurum || '-'}</span>
      <b>Poliçe No</b><span>${d.polNo || '-'}</span>
      <b>Provizyon No</b><span>${d.provNo || '-'}</span>
      <b>Tarife</b><span>${d.tarife || '-'}</span>
      <b>Hizmet Noktası</b><span>${d.doktor || '-'}</span>
    </div>
    <h2>Planlanan / Yapılan Hizmetler</h2>
    <p>${d.satirlar.map(s => s.hizmet).join(', ') || '-'}</p>
    <div class="toplamlar">
      <div><span>Hasta Payı</span><span>${para(d.genelToplamHasta)} TL</span></div>
      <div class="genel"><span>Kurum Payı</span><span>${para(d.genelToplamKurum)} TL</span></div>
    </div>
    <div class="imza"><div>Hasta / Yakını İmza</div><div>Kurum Yetkilisi</div></div>`);
};

const barkodYazdir = async (d) => {
  const JsBarcode = (await import('jsbarcode')).default;
  const kod = String(d.masterProtNo).padStart(10, '0');   // VB6 TAMAMLA: 10 haneye sıfır doldurma
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, kod, { format: 'CODE128', width: 2, height: 48, fontSize: 13, margin: 4 });
  const yas = d.dogTar ? Math.max(0, new Date().getFullYear() - Number(String(d.dogTar).slice(0, 4))) : '';
  yazdirPenceresi('Hasta Barkodu', `
    <div style="width:70mm;border:1px solid #1F2F46;border-radius:4px;padding:6px 8px;font-size:11px">
      <div style="font-weight:700;font-size:13px">${d.adSoy}</div>
      <div>${d.tcKimlik || ''} ${d.dogTar ? '· ' + trTarih(d.dogTar) : ''} ${yas !== '' ? '· ' + yas : ''} ${d.cins || ''}</div>
      <div>Protokol: ${d.masterProtNo} · ${trTarih(d.tar)}</div>
      <div style="margin-top:4px">${svg.outerHTML}</div>
    </div>`);
};

// --- KABUL DETAY PANELİ (VB6 SSPanel7) ---------------------------------------------------
// Liste ekranının üzerinde açılır; VB6'daki gibi yalnızca GEÇİCİ KAYIT veya HASTA ÇIKIŞI ile
// kapanır (kullanıcı kararı: 2026-09-21). Tüm yardımcı pencereler modaldir ve aynı anda
// yalnızca biri açıktır.
function KabulDetayi({ detay, secenek, onKapat }) {
  const yetkiler = secenek?.yetkiler || {};
  const [d, setD] = useState(detay);
  const [bankoNot, setBankoNot] = useState(detay.bankoNot || '');
  const [polNo, setPolNo] = useState(detay.polNo || '');
  const [provNo, setProvNo] = useState(detay.provNo || '');
  const [yuzdeHasta, setYuzdeHasta] = useState(detay.yuzdeHasta);
  const [yuzdeKurum, setYuzdeKurum] = useState(detay.yuzdeKurum);
  const [secSatir, setSecSatir] = useState(null);      // seçili hizmet satırı (KOD)
  const [modal, setModal] = useState(null);            // { tur, satir }
  const [islemde, setIslemde] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');

  const kurumlu = d.tarfTp !== 1;           // VB6: KABUL_TARFTP = 1 ise kurum payı yok
  const kilitli = d.cikisYapildi || d.tahsilatYasagi;
  // Ekran içi yetkiler VB6 ile birebir (TEKN kolonları).
  const yEkle = !!yetkiler.hizmetEkle;        // RANDEVU1
  const yCikis = !!yetkiler.hastaCikisi;      // RANDEVU2
  const yFiyat = !!yetkiler.fiyatDegistir;    // RANDEVU3
  const yKdv = !!yetkiler.kdvDegistir;        // RAPOR2
  const yIptal = !!yetkiler.kabulIptal;       // RAPOR4
  const yToplam = !!yetkiler.genelToplamDuzelt || !!yetkiler.genelToplamSinirli;  // RAPOR6 / YETK41
  const yPay = !!yetkiler.payYuzdesi;         // RAPOR8
  const yTahsilat = !!yetkiler.tahsilat;      // MUA

  useEffect(() => {
    if (!bilgi) return undefined;
    const z = setTimeout(() => setBilgi(''), 5000);
    return () => clearTimeout(z);
  }, [bilgi]);

  const yenile = useCallback(async (mesaj) => {
    try {
      const { data } = await api.get(`/banko/kabul/${d.masterProtNo}/detay`);
      setD(data);
      setYuzdeHasta(data.yuzdeHasta);
      setYuzdeKurum(data.yuzdeKurum);
      if (mesaj) setBilgi(mesaj);
      setHata('');
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kabul yenilenemedi.');
    }
  }, [d.masterProtNo]);

  // Tüm sunucu çağrıları buradan geçer: hata mesajı tek yerde gösterilir, pencere kapanır, liste yenilenir.
  const calistir = async (istek, basariMesaji, { kapat = true, yenileDetay = true } = {}) => {
    setHata(''); setIslemde(true);
    try {
      const yanit = await istek();
      if (kapat) setModal(null);
      if (yenileDetay) await yenile(basariMesaji);
      else setBilgi(basariMesaji);
      return yanit?.data ?? true;
    } catch (err) {
      setHata(err.response?.data?.hata || 'İşlem yapılamadı.');
      return null;
    } finally {
      setIslemde(false);
    }
  };

  const geciciKayit = () => calistir(
    () => api.post(`/banko/kabul/${d.masterProtNo}/gecici-kayit`, { bankoNot, polNo, provNo, yuzdeHasta, yuzdeKurum }),
    null, { kapat: false, yenileDetay: false },
  ).then(ok => { if (ok) onKapat(`${d.adSoy} — kabul geçici olarak kaydedildi.`); });

  // VB6 SSCommand10 (F4): faturayı kes, kabulü kapat.
  const hastaCikisi = async () => {
    setHata('');
    const bugunMu = trTarih(d.tar) === new Date().toLocaleDateString('tr-TR');
    const satirlar = [
      `${d.adSoy} isimli hastanın ÇIKIŞ işlemini yaparak kaydı kapatacaksınız.`,
      'Kapatılan bir kayda yeni hizmet eklenemez.',
      '',
      `Hasta payı genel toplam : ${para(d.genelToplamHasta)} TL`,
    ];
    if (kurumlu) satirlar.push(`Kurum payı genel toplam : ${para(d.genelToplamKurum)} TL  (${d.kurum})`);
    satirlar.push(`Alınan tahsilat : ${para(d.alinan)} TL · Kalan : ${para(d.kalan)} TL`);
    if (!bugunMu) satirlar.push('', `DİKKAT: Bu kayıt ${trTarih(d.tar)} tarihine işlenecek, bugüne değil.`);
    satirlar.push('', 'Onaylıyor musunuz?');
    if (!window.confirm(satirlar.join('\n'))) return;

    setIslemde(true);
    try {
      const { data } = await api.post(`/banko/kabul/${d.masterProtNo}/hasta-cikisi`, {
        bankoNot, polNo, provNo, yuzdeHasta, yuzdeKurum, onaylandi: true,
      });
      const ek = data.kurumProtNo ? ` · Kurum faturası: ${data.kurumProtNo}` : '';
      onKapat(`${d.adSoy} — çıkış yapıldı. Hasta faturası: ${data.hastaProtNo}${ek}`);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Hasta çıkışı yapılamadı.');
      setIslemde(false);
    }
  };

  const hizmetSil = (s) => {
    if (!window.confirm(`${s.hizmet} hizmetini iptal etmek istiyor musunuz?`)) return;
    calistir(() => api.delete(`/banko/kabul/${d.masterProtNo}/hizmet/${s.kod}`), 'Hizmet iptal edildi.', { kapat: false });
  };

  const kabulIptal = async () => {
    const ok = window.confirm(
      `Bu işlemle ${d.adSoy} isimli hastanın hizmet listesi silinerek KABUL işlemi iptal edilecek.\n`
      + 'Randevu kaydı da silinir ve hak yeniden kullanılabilir hâle gelir.\n\nOnaylıyor musunuz?');
    if (!ok) return;
    setHata(''); setIslemde(true);
    try {
      await api.post(`/banko/kabul/${d.masterProtNo}/iptal`);
      onKapat(`${d.adSoy} — kabul iptal edildi.`);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kabul iptal edilemedi.');
      setIslemde(false);
    }
  };

  // Izgara hücresine tıklayınca ilgili düzenleme penceresi açılır (VB6 Grid3_DblClick).
  const hucreAc = (tur, s) => {
    if (kilitli) return;
    if ((tur === 'fiyatHasta' || tur === 'fiyatKurum') && !yFiyat) { setHata('Fiyat değişikliği için yetkili değilsiniz.'); return; }
    if (tur === 'kdv' && !yKdv) { setHata('KDV değişikliği için yetkili değilsiniz.'); return; }
    if (tur === 'fiyatKurum' && !kurumlu) return;
    setSecSatir(s.kod);
    setModal({ tur, satir: s });
  };

  const satirGuncelle = (kod, govde) =>
    calistir(() => api.put(`/banko/kabul/${d.masterProtNo}/hizmet/${kod}`, govde), 'Satır güncellendi.');

  return (
    <div className="hkb-detay-zemin">
      <div className="hkb-detay">
        <div className="hkb-detay-bas">
          <div className="hkb-detay-hasta">
            <strong>{d.adSoy}</strong>
            <span>Hasta No: {d.kayitNo}{d.tcKimlik ? ` · TC: ${d.tcKimlik}` : ''}</span>
            <span>Protokol: {d.masterProtNo} · Onay Tarihi: {trTarih(d.tar)}</span>
          </div>
          <div className="hkb-detay-tarife">
            <span className="hkb-rozet">{TARIFE_TIPI[d.tarfTp] || '—'}</span>
            <span>{d.tarife}</span>
            {kurumlu && <span>Kurum: {d.kurum}</span>}
            <span>Hizmet Noktası: {d.doktor}</span>
          </div>
        </div>

        {(hata || bilgi || kilitli) && (
          <div className={`hkb-detay-uyari ${hata ? 'hata' : bilgi ? 'bilgi' : ''}`}>
            {hata || bilgi || (d.cikisYapildi
              ? 'Bu kabulün çıkışı yapılmış (faturası kesilmiş); kayıtta değişiklik yapılamaz.'
              : 'Bu seansın tahsilatı SATIŞ ESNASINDA alınmıştır, kayıtta değişiklik yapılamaz.')}
          </div>
        )}

        <div className="hkb-detay-govde">
          <div className="hkb-detay-sol">
            <div className="hkb-sepet-arac">
              <span className="hkb-kutu-baslik">Yapılan İşlemler</span>
              <span className="hkb-ipucu">Düzenlemek için hücreye tıklayın</span>
              <button type="button" className="hkb-sec" disabled={kilitli || !yFiyat || islemde}
                onClick={() => calistir(() => api.post(`/banko/kabul/${d.masterProtNo}/orijinal-fiyatlar`),
                  'Fiyatlar tarifedeki orijinal hâline döndürüldü.', { kapat: false })}
                title={yFiyat ? 'Elle girilen fiyatları temizle, tarifeden yeniden hesapla' : 'Bu işlem için yetkili değilsiniz.'}>
                Orjinal Fiyatlara Dön
              </button>
              {/* VB6 çıktıları: Excel proforma, Kabul Raporu, Ön Sigorta Bilgi Formu, Barkod. */}
              <button type="button" className="hkb-sec" onClick={() => proformaExcel(d, true)}
                disabled={d.satirlar.length === 0}>Proforma (Hasta)</button>
              {kurumlu && (
                <button type="button" className="hkb-sec" onClick={() => proformaExcel(d, false)}
                  disabled={d.satirlar.length === 0}>Proforma (Kurum)</button>
              )}
              <button type="button" className="hkb-sec" onClick={() => kabulRaporu(d, true)}
                disabled={d.satirlar.length === 0}>Kabul Raporu</button>
              {kurumlu && (
                <button type="button" className="hkb-sec" onClick={() => onSigortaFormu(d)}>Ön Sigorta Formu</button>
              )}
              <button type="button" className="hkb-sec" onClick={() => barkodYazdir(d)}>Barkod</button>
            </div>

            <div className="hkb-sepet">
              <table>
                <thead>
                  <tr>
                    <th>İşlem Tarihi</th>
                    <th>Yapılan İşlemler</th>
                    <th className="sag">Süre</th>
                    <th>Rand. Tarih</th>
                    <th>Rand. Saat</th>
                    <th className="sag">Adet</th>
                    <th className="sag">KDV %</th>
                    <th className="sag">B.Fiyat (Hasta)</th>
                    <th className="sag">Tutar (Hasta)</th>
                    {kurumlu && <th className="sag">B.Fiyat (Kurum)</th>}
                    {kurumlu && <th className="sag">Tutar (Kurum)</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.satirlar.length === 0 && (
                    <tr><td colSpan={kurumlu ? 12 : 10} className="hkb-bos">
                      Bu kabulde hizmet satırı yok. "İŞLEM EKLE" ile hizmet ekleyiniz.
                    </td></tr>
                  )}
                  {d.satirlar.map(s => (
                    <tr key={s.kod}
                      className={`${secSatir === s.kod ? 'secili' : ''} ${s.neKat === 'STOK' ? 'stok' : (s.grupKod === 1 ? 'muayene' : '')}`}
                      onClick={() => setSecSatir(s.kod)}>
                      <td className="tikla" onClick={() => hucreAc('tarih', s)}>{trTarih(s.tar)}</td>
                      <td className="hkb-ad" title={s.paketAdi || ''}>
                        {s.hizmet}{s.drm === 1 ? ' ✓' : ''}
                      </td>
                      <td className="sag tikla" onClick={() => hucreAc('sure', s)}>{s.sure || ''}</td>
                      <td>{s.randevuTar}</td>
                      <td>{s.randevuSaat}</td>
                      <td className="sag tikla" onClick={() => hucreAc('adet', s)}>{s.adet}</td>
                      <td className="sag tikla" onClick={() => hucreAc('kdv', s)}>{s.kdvYd}</td>
                      <td className="sag hasta tikla" onClick={() => hucreAc('fiyatHasta', s)}>{para(s.birimHasta)}</td>
                      <td className="sag hasta">{para(s.tutarHasta)}</td>
                      {kurumlu && <td className="sag kurum tikla" onClick={() => hucreAc('fiyatKurum', s)}>{para(s.birimKurum)}</td>}
                      {kurumlu && <td className="sag kurum">{para(s.tutarKurum)}</td>}
                      <td className="hkb-satir-sil">
                        <button type="button" onClick={() => hizmetSil(s)} disabled={kilitli || !yEkle || islemde}
                          title={yEkle ? 'Hizmeti iptal et' : 'Bu işlem için yetkili değilsiniz.'}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="hkb-detay-alanlar">
              <label className="hkb-not">
                <span>Banko Notu</span>
                <textarea rows={2} value={bankoNot} maxLength={250} disabled={kilitli}
                  onChange={e => setBankoNot(e.target.value)} />
              </label>
              {kurumlu && (
                <div className="hkb-detay-kurumalan">
                  <label><span>Poliçe No</span>
                    <input value={polNo} maxLength={50} disabled={kilitli} onChange={e => setPolNo(e.target.value)} /></label>
                  <label><span>Provizyon No</span>
                    <input value={provNo} maxLength={50} disabled={kilitli} onChange={e => setProvNo(e.target.value)} /></label>
                  <label><span>Hasta Payı %</span>
                    <input type="number" min={0} max={100} value={yuzdeHasta} disabled={kilitli || !(yPay || yFiyat)}
                      onChange={e => setYuzdeHasta(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></label>
                  <label><span>Kurum Payı %</span>
                    <input type="number" min={0} max={100} value={yuzdeKurum} disabled={kilitli || !(yPay || yFiyat)}
                      onChange={e => setYuzdeKurum(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></label>
                  <button type="button" className="hkb-sec" disabled={islemde || kilitli || !(yPay || yFiyat)}
                    onClick={() => calistir(() => api.post(`/banko/kabul/${d.masterProtNo}/paylari-uygula`,
                      { yuzdeHasta, yuzdeKurum }), 'Hasta / kurum payları uygulandı.', { kapat: false })}
                    title={yPay || yFiyat ? 'Yüzdelere göre fiyatları yeniden hesapla' : 'Bu işlem için yetkili değilsiniz.'}>
                    Payları Uygula
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="hkb-detay-sag">
            <div className="hkb-toplam hasta">
              <span className="hkb-kutu-baslik">Hasta Payı</span>
              <div className="hkb-toplam-satir"><span>TOPLAM</span><b>{para(d.toplamHasta)}</b></div>
              {d.kdvHasta.map(x => (
                <div key={x.oran} className="hkb-toplam-satir kdv"><span>KDV %{x.oran}</span><b>{para(x.tutar)}</b></div>
              ))}
              <div className={`hkb-toplam-satir genel ${kilitli || !yToplam ? '' : 'tikla'}`}
                onClick={() => { if (!kilitli && yToplam) setModal({ tur: 'genelToplamHasta' }); }}
                title={yToplam ? 'Genel toplamı düzenle (indirim / arttırım)' : 'Genel toplamı düzenleme yetkiniz yok.'}>
                <span>GENEL TOPLAM</span><b>{para(d.genelToplamHasta)}</b>
              </div>
              <div className="hkb-toplam-satir"><span>ALINAN</span><b>{para(d.alinan)}</b></div>
              <div className="hkb-toplam-satir kalan"><span>KALAN</span><b>{para(d.kalan)}</b></div>
            </div>

            {kurumlu && (
              <div className="hkb-toplam kurum">
                <span className="hkb-kutu-baslik">Kurum Payı</span>
                <div className="hkb-toplam-satir"><span>TOPLAM</span><b>{para(d.toplamKurum)}</b></div>
                {d.kdvKurum.map(x => (
                  <div key={x.oran} className="hkb-toplam-satir kdv"><span>KDV %{x.oran}</span><b>{para(x.tutar)}</b></div>
                ))}
                <div className={`hkb-toplam-satir genel ${kilitli || !yToplam ? '' : 'tikla'}`}
                  onClick={() => { if (!kilitli && yToplam) setModal({ tur: 'genelToplamKurum' }); }}>
                  <span>GENEL TOPLAM</span><b>{para(d.genelToplamKurum)}</b>
                </div>
              </div>
            )}

            <div className="hkb-detay-durum">
              {d.geldi === 1
                ? <span className="hkb-geldi-rozet">Hasta randevusuna GELDİ</span>
                : <span className="hkb-gelmedi-rozet">Hasta henüz GELMEDİ olarak işaretli</span>}
            </div>
          </div>
        </div>

        <div className="hkb-detay-butonlar">
          <button type="button" className="hkb-buyukbuton ekle" onClick={() => setModal({ tur: 'hizmet' })}
            disabled={islemde || kilitli || !yEkle}
            title={yEkle ? 'Sepete hizmet, malzeme veya paket ekle' : 'Bu işlem için yetkili değilsiniz.'}>
            <b>➕</b><span>İŞLEM EKLE<small>Hizmet / malzeme / paket</small></span>
          </button>
          <button type="button" className="hkb-buyukbuton tahsilat" onClick={() => setModal({ tur: 'tahsilat' })}
            disabled={islemde || kilitli || !yTahsilat || d.kalan <= 0}
            title={!yTahsilat ? 'Bu işlem için yetkili değilsiniz.' : d.kalan <= 0 ? 'Bu geliş için tahsilat tamamlanmış.' : 'Hastadan ödeme al'}>
            <b>💰</b><span>TAHSİLAT<small>{d.kalan > 0 ? `Kalan ${para(d.kalan)} TL` : 'Tahsilat tamam'}</small></span>
          </button>
          <button type="button" className="hkb-buyukbuton iptal" onClick={kabulIptal}
            disabled={islemde || !yIptal || d.cikisYapildi}
            title={yIptal ? 'Kabulü ve randevuyu iptal et' : 'Bu işlem için yetkili değilsiniz.'}>
            <b>🗑️</b><span>KABUL İPTALİ<small>Kaydı tamamen sil</small></span>
          </button>
          <button type="button" className="hkb-buyukbuton hizmetiptal"
            onClick={() => {
              const s = d.satirlar.find(x => x.kod === secSatir);
              if (!s) { setHata('Lütfen iptal edilecek hizmeti listeden seçiniz.'); return; }
              hizmetSil(s);
            }}
            disabled={islemde || kilitli || !yEkle || d.satirlar.length === 0}
            title={yEkle ? 'Seçili hizmeti sepetten çıkar' : 'Bu işlem için yetkili değilsiniz.'}>
            <b>❌</b><span>HİZMET İPTALİ<small>Seçili satırı çıkar</small></span>
          </button>
          <button type="button" className="hkb-buyukbuton gecici" onClick={geciciKayit} disabled={islemde || kilitli}>
            <b>💾</b><span>GEÇİCİ KAYIT<small>Kaydı sonra devam etmek üzere sakla</small></span>
          </button>
          <button type="button" className="hkb-buyukbuton cikis" onClick={hastaCikisi}
            disabled={islemde || !yCikis || d.cikisYapildi || d.geldi !== 1 || d.satirlar.length === 0}
            title={!yCikis ? 'Bu işlem için yetkili değilsiniz.'
              : d.geldi !== 1 ? 'Hasta GELDİ olarak işaretlenmeden çıkış yapılamaz.' : 'Faturayı kes, kabulü kapat'}>
            <b>🚪</b><span>HASTA ÇIKIŞI<small>Faturayı kes, kabulü kapat</small></span>
          </button>
          {(!yCikis || d.geldi !== 1 || d.satirlar.length === 0) && !d.cikisYapildi && (
            <div className="hkb-buton-not">
              Hasta çıkışı şu an yapılamıyor: {!yCikis ? 'bu işlem için yetkiniz yok.'
                : d.geldi !== 1 ? 'hasta randevusuna GELDİ olarak işaretlenmemiş (Randevu Ekranı\'ndan işaretleyin).'
                  : 'kabulde hizmet satırı yok, önce İŞLEM EKLE ile hizmet ekleyin.'}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <DetayPenceresi
          modal={modal} d={d} secenek={secenek} islemde={islemde}
          onKapat={() => setModal(null)}
          onSatirGuncelle={satirGuncelle}
          onCalistir={calistir}
        />
      )}
    </div>
  );
}

// --- Detay panelinin açılır pencereleri (VB6 SSPanel1/2/9/_Op_3/_Op_7/_Op_16/_Op_2 + HZMTLIST + HIZLITAH)
function DetayPenceresi({ modal, d, secenek, islemde, onKapat, onSatirGuncelle, onCalistir }) {
  const s = modal.satir;
  const [deger, setDeger] = useState(() => {
    switch (modal.tur) {
      case 'adet': return String(s?.adet ?? 1);
      case 'sure': return String(s?.sure ?? 20);
      case 'tarih': return isoTarih(s?.tar) || isoBugun();
      case 'kdv': return String(s?.kdvYd ?? 0);
      case 'fiyatHasta': return String(s?.birimHasta ?? 0);
      case 'fiyatKurum': return String(s?.birimKurum ?? 0);
      case 'genelToplamHasta': return String(d.genelToplamHasta);
      case 'genelToplamKurum': return String(d.genelToplamKurum);
      default: return '';
    }
  });
  const [tumune, setTumune] = useState(false);
  const [kdvDahil, setKdvDahil] = useState(false);

  // İşlem Ekle penceresi
  const [ara, setAra] = useState('');
  const [liste, setListe] = useState([]);
  const [secHizmet, setSecHizmet] = useState(null);
  const [ekAdet, setEkAdet] = useState(1);
  const [ekTarih, setEkTarih] = useState(isoTarih(d.tar) || isoBugun());

  // Tahsilat penceresi
  const [hesaplar, setHesaplar] = useState(null);
  const [tahTutar, setTahTutar] = useState(String(d.kalan > 0 ? d.kalan : 0));
  const [tahTur, setTahTur] = useState('nakit');
  const [tahHesap, setTahHesap] = useState(0);

  useEffect(() => {
    if (modal.tur !== 'hizmet') return undefined;
    let iptal = false;
    const z = setTimeout(async () => {
      try {
        const { data } = await api.get(`/banko/kabul/${d.masterProtNo}/hizmet-listesi`, { params: { ara } });
        if (!iptal) setListe(data);
      } catch { if (!iptal) setListe([]); }
    }, 250);
    return () => { iptal = true; clearTimeout(z); };
  }, [modal.tur, ara, d.masterProtNo]);

  useEffect(() => {
    if (modal.tur !== 'tahsilat') return;
    api.get('/banko/kabul/tahsilat-hesaplari')
      .then(({ data }) => setHesaplar(data))
      .catch(() => setHesaplar({ kasa: null, kartlar: [], bankalar: [] }));
  }, [modal.tur]);

  const sayi = () => Number(String(deger).replace(',', '.')) || 0;

  const baslik = {
    adet: 'Adet', sure: 'Süre (dk)', tarih: 'İşlem Tarihi', kdv: 'KDV %',
    fiyatHasta: 'Birim Fiyat ( Hasta Payı )', fiyatKurum: 'Birim Fiyat ( Kurum Payı )',
    genelToplamHasta: 'Genel Toplamı Düzenleyiniz ( Hasta )',
    genelToplamKurum: 'Genel Toplamı Düzenleyiniz ( Kurum )',
    hizmet: 'İşlem Ekle', tahsilat: 'Tahsilat',
  }[modal.tur] || '';

  const uygula = () => {
    switch (modal.tur) {
      case 'adet': return onSatirGuncelle(s.kod, { adet: Math.max(1, Math.round(sayi())), tumune });
      case 'sure': return onSatirGuncelle(s.kod, { sure: Math.max(0, Math.round(sayi())) });
      case 'tarih': return onSatirGuncelle(s.kod, { tar: deger });
      case 'kdv': return onSatirGuncelle(s.kod, { kdvYd: Math.round(sayi()), tumune });
      case 'fiyatHasta': return onSatirGuncelle(s.kod, { birimHasta: sayi(), kdvDahil });
      case 'fiyatKurum': return onSatirGuncelle(s.kod, { birimKurum: sayi(), kdvDahil });
      case 'genelToplamHasta':
      case 'genelToplamKurum':
        return onCalistir(() => api.post(`/banko/kabul/${d.masterProtNo}/genel-toplam`,
          { hedef: sayi(), hasta: modal.tur === 'genelToplamHasta' }), 'Genel toplam yeniden dağıtıldı.');
      case 'hizmet':
        if (!secHizmet) return undefined;
        return onCalistir(() => api.post(`/banko/kabul/${d.masterProtNo}/hizmet`,
          { kod: secHizmet.kod, tur: secHizmet.tur, adet: ekAdet, tarih: ekTarih }), 'Hizmet eklendi.');
      case 'tahsilat':
        return onCalistir(() => api.post(`/banko/kabul/${d.masterProtNo}/tahsilat`,
          { tutar: Number(String(tahTutar).replace(',', '.')) || 0, tur: tahTur, hesapKod: tahHesap }),
          'Tahsilat alındı.');
      default: return undefined;
    }
  };

  const tusla = (e) => {
    if (e.key === 'Escape') onKapat();
    if (e.key === 'Enter' && modal.tur !== 'hizmet') { e.preventDefault(); uygula(); }
  };

  const genis = modal.tur === 'hizmet';

  return (
    <div className="hkb-ic-modal-zemin" onKeyDown={tusla} role="presentation">
      <div className={`hkb-ic-modal ${genis ? 'genis' : ''}`}>
        <div className="hkb-ic-modal-bas">
          <strong>{baslik}</strong>
          <button type="button" onClick={onKapat}>✕</button>
        </div>
        <div className="hkb-ic-modal-govde">
          {s && !['hizmet', 'tahsilat'].includes(modal.tur) && (
            <p className="hkb-ic-hizmet">{s.hizmet}</p>
          )}

          {['adet', 'sure'].includes(modal.tur) && (
            <input type="number" min={modal.tur === 'adet' ? 1 : 0} autoFocus value={deger}
              onChange={e => setDeger(e.target.value)} />
          )}

          {modal.tur === 'tarih' && (
            <input type="date" autoFocus value={deger} onChange={e => setDeger(e.target.value)} />
          )}

          {modal.tur === 'kdv' && (
            <div className="hkb-kdv-secim">
              {(secenek?.kdvOranlari || [0, 10, 20]).map(o => (
                <button key={o} type="button" className={`hkb-sec ${Number(deger) === o ? 'aktif' : ''}`}
                  onClick={() => setDeger(String(o))}>%{o}</button>
              ))}
            </div>
          )}

          {['fiyatHasta', 'fiyatKurum'].includes(modal.tur) && (
            <>
              <input type="number" step="0.01" min={0} autoFocus value={deger} onChange={e => setDeger(e.target.value)} />
              <div className="hkb-kdv-secim">
                <button type="button" className={`hkb-sec ${!kdvDahil ? 'aktif' : ''}`} onClick={() => setKdvDahil(false)}>KDV Hariç</button>
                <button type="button" className={`hkb-sec ${kdvDahil ? 'aktif' : ''}`} onClick={() => setKdvDahil(true)}>KDV Dahil</button>
              </div>
              <p className="hkb-ic-not">Girilen fiyat {kdvDahil ? 'KDV dahil kabul edilir ve %' + (s?.kdvYd ?? 0) + ' KDV ayrıştırılır' : 'KDV hariç kaydedilir'}.</p>
            </>
          )}

          {['genelToplamHasta', 'genelToplamKurum'].includes(modal.tur) && (
            <>
              <input type="number" step="0.01" min={0} autoFocus value={deger} onChange={e => setDeger(e.target.value)} />
              <p className="hkb-ic-not">
                Mevcut genel toplam: <b>{para(modal.tur === 'genelToplamHasta' ? d.genelToplamHasta : d.genelToplamKurum)} TL</b>.
                Girdiğiniz tutara göre satır fiyatları orantılı olarak yeniden hesaplanır.
                {secenek?.maxIskonto > 0 && !secenek?.yetkiler?.genelToplamDuzelt
                  ? ` İndirim sınırınız: ${para(secenek.maxIskonto)} TL.` : ''}
              </p>
            </>
          )}

          {modal.tur === 'hizmet' && (
            <>
              <input autoFocus placeholder="Hizmet, malzeme ya da paket ara..." value={ara}
                onChange={e => setAra(e.target.value)} />
              <div className="hkb-hizmet-liste">
                <table>
                  <thead><tr><th>Adı</th><th>Tür</th><th>Grup</th><th className="sag">Süre</th><th className="sag">KDV</th><th className="sag">Fiyat</th></tr></thead>
                  <tbody>
                    {liste.length === 0 && <tr><td colSpan={6} className="hkb-bos">Kayıt yok.</td></tr>}
                    {liste.map(h => (
                      <tr key={`${h.tur}-${h.kod}`} className={secHizmet?.kod === h.kod && secHizmet?.tur === h.tur ? 'secili' : ''}
                        onClick={() => setSecHizmet(h)} onDoubleClick={() => { setSecHizmet(h); }}>
                        <td className="hkb-ad">{h.adi}</td>
                        <td>{h.tur === 'stok' ? 'Malzeme' : h.tur === 'paket' ? 'Paket' : 'Hizmet'}</td>
                        <td>{h.grup}</td>
                        <td className="sag">{h.sure || ''}</td>
                        <td className="sag">{h.kdvYd || ''}</td>
                        <td className="sag">{h.tur === 'paket' ? '' : para(h.fiyat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="hkb-ic-satir">
                <label><span>Adet</span>
                  <input type="number" min={1} value={ekAdet} onChange={e => setEkAdet(Math.max(1, Number(e.target.value) || 1))} /></label>
                <label><span>İşlem Tarihi</span>
                  <input type="date" value={ekTarih} onChange={e => setEkTarih(e.target.value)} /></label>
              </div>
            </>
          )}

          {modal.tur === 'tahsilat' && (
            <>
              <div className="hkb-ic-satir">
                <label><span>Tutar (TL)</span>
                  <input type="number" step="0.01" min={0} autoFocus value={tahTutar}
                    onChange={e => setTahTutar(e.target.value)} /></label>
                <span className="hkb-ic-not">Genel toplam {para(d.genelToplamHasta)} · Alınan {para(d.alinan)} · Kalan {para(d.kalan)}</span>
              </div>
              <div className="hkb-kdv-secim">
                {[['nakit', 'Nakit'], ['kart', 'Kredi Kartı'], ['banka', 'Banka / Havale']].map(([k, ad]) => (
                  <button key={k} type="button" className={`hkb-sec ${tahTur === k ? 'aktif' : ''}`}
                    onClick={() => { setTahTur(k); setTahHesap(0); }}>{ad}</button>
                ))}
              </div>
              {tahTur === 'nakit' && (
                <p className="hkb-ic-not">
                  {hesaplar?.kasa ? `Kasa: ${hesaplar.kasa.adi}` : 'Bu şubede kasanız (vezne) tanımlı değil.'}
                </p>
              )}
              {tahTur !== 'nakit' && (
                <select value={tahHesap} onChange={e => setTahHesap(Number(e.target.value))}>
                  <option value={0}>{tahTur === 'kart' ? 'POS seçiniz...' : 'Banka hesabı seçiniz...'}</option>
                  {((tahTur === 'kart' ? hesaplar?.kartlar : hesaplar?.bankalar) || []).map(h => (
                    <option key={h.kod} value={h.kod}>{h.adi}</option>
                  ))}
                </select>
              )}
            </>
          )}

          {['adet', 'kdv'].includes(modal.tur) && (
            <label className="hkb-tumune">
              <input type="checkbox" checked={tumune} onChange={e => setTumune(e.target.checked)} />
              <span>Tüm hizmet satırlarına uygula</span>
            </label>
          )}
        </div>
        <div className="hkb-ic-modal-alt">
          <button type="button" className="btn" onClick={onKapat}>Vazgeç</button>
          <button type="button" className="btn btn-primary" onClick={uygula}
            disabled={islemde || (modal.tur === 'hizmet' && !secHizmet)}>
            {modal.tur === 'hizmet' ? 'Sepete Ekle' : modal.tur === 'tahsilat' ? 'Tahsilatı Al' : 'Uygula'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HastaKabul;
